<script setup lang="ts">
import { useTemplateRef } from "vue";
import { useIsOffline } from "../../composables/useIsOffline";
import {
  CriterionResultUserImpact,
  NotCompliantItem,
  PatchNotCompliantItemData
} from "../../types";
import { formatUserImpact } from "../../utils";
import RichTextEditor from "../tiptap/RichTextEditor.vue";
import DsfrField from "../ui/DsfrField.vue";
import { RadioColor } from "../ui/Radio.vue";
import RadioGroup from "../ui/RadioGroup.vue";

const props = defineProps<{
  index: number;
  item: NotCompliantItem;
  canDelete: boolean;
}>();

const emit = defineEmits<{
  (e: "delete", id: number): void;
  (e: "update", patch: PatchNotCompliantItemData, debounce: boolean): void;
}>();

const baseTitle = "Erreur";

function handleItemChange(
  field: keyof NotCompliantItem,
  value: NotCompliantItem[keyof NotCompliantItem]
) {
  emit(
    "update",
    { id: props.item.id, [field]: value },
    field === "title" || field === "comment"
  );
}

const userImpacts: Array<{
  label: string;
  value: CriterionResultUserImpact;
  color?: RadioColor;
}> = [
  {
    value: CriterionResultUserImpact.BLOCKING,
    label: formatUserImpact(CriterionResultUserImpact.BLOCKING),
    color: RadioColor.RED
  },
  {
    value: CriterionResultUserImpact.MAJOR,
    label: formatUserImpact(CriterionResultUserImpact.MAJOR),
    color: RadioColor.YELLOW
  },
  {
    value: CriterionResultUserImpact.MINOR,
    label: formatUserImpact(CriterionResultUserImpact.MINOR),
    color: RadioColor.GREY
  }
];

const isOffline = useIsOffline();

const commentEditorRef = useTemplateRef<InstanceType<typeof RichTextEditor>>("commentEditorRef");
const titleEditorRef = useTemplateRef<InstanceType<typeof DsfrField>>("titleEditorRef");

defineExpose({
  textFocusEditor: () => {
    titleEditorRef.value?.focus();
  },
  commentFocusEditor: () => {
    commentEditorRef.value?.focusEditor();
  }
});
</script>

<template>
  <div class="fr-grid-row">
    <div class="fr-col-8 fr-mb-2v">
      <p class="fr-mb-0 fr-text--bold">
        {{ baseTitle }} {{ index + 1 }}
      </p>
    </div>
    <div v-if="canDelete" class="fr-col-4 error-user-delete">
      <button
        type="button"
        class="fr-btn fr-btn--sm fr-btn--tertiary-no-outline"
        @click="emit('delete', item.id)"
      >Supprimer<span class="fr-sr-only">&nbsp;l'erreur {{ index + 1 }}</span></button>
    </div>
  </div>

  <DsfrField
    :id="`error-title-${item.id}-${index}`"
    ref="titleEditorRef"
    :model-value="item.title ?? undefined"
    type="text"
    label="Titre de l’erreur"
    :label-sr-only="`${index + 1}`"
    class="fr-mb-2w user-error-label"
    @input="handleItemChange('title', $event.target.value)"
  />

  <RichTextEditor
    :id="`error-comment-${item.id}-${index}`"
    ref="commentEditorRef"
    type="criterium"
    :model-value="item.comment"
    :label="`Recommandations sur l'erreur ${index + 1}`"
    class="fr-mb-1w"
    description="Description de l’erreur et proposition de correction"
    @update:model-value="handleItemChange('comment', $event)"
  />

  <!-- USER IMPACT -->
  <RadioGroup
    ref="userImpactRadioGroupRef"
    class="fr-mb-5v"
    tabindex="-1"
    :items="userImpacts"
    :default-value="null"
    :disabled="isOffline"
    :model-value="item.userImpact"
    @update:model-value="handleItemChange('userImpact', $event)"
  >
    <template #label>
      <div class="user-impact-label">
        <span>Impact sur l’usager</span>
        <button
          aria-describedby="tooltip"
          type="button"
          class="fr-btn fr-btn--tooltip fr-btn--sm fr-icon-question-line fr-btn--tertiary-no-outline"
          data-fr-js-tooltip-referent="true"
        >
          Informations sur l’impact usager
        </button>

        <div
          id="tooltip"
          class="fr-tooltip fr-placement"
          role="tooltip"
          data-fr-js-tooltip="true"
        >
          <p class="fr-text--xs fr-mb-1w">
            <strong>Bloquant</strong> : empêche complètement l’accès ou
            l’utilisation.<br />
            <span class="user-impact-example">Ex : il est impossible de soumettre un formulaire au
              clavier.</span>
          </p>
          <p class="fr-text--xs fr-mb-1w">
            <strong>Majeur</strong> : rend l’accès ou l’utilisation
            difficile.<br />
            <span class="user-impact-example">Ex : les champs ne sont pas regroupés.</span>
          </p>
          <p class="fr-text--xs fr-mb-0">
            <strong>Mineur</strong> : gêne légèrement sans empêcher l’accès ou
            l’utilisation.<br />
            <span class="user-impact-example">Ex : des retours à la ligne sont utilisés pour espacer des
              textes.</span>
          </p>
        </div>
      </div>
    </template>
  </RadioGroup>

  <!-- QUICK WIN -->
  <div class="fr-fieldset__element fr-fieldset__element--inline fr-mb-0">
    <div class="fr-checkbox-group">
      <input
        :id="`criterium-quick-win-${item.id}-${index}`"
        :checked="item.quickWin"
        type="checkbox"
        @input="handleItemChange('quickWin', ($event.target as HTMLInputElement).checked)"
      />
      <label class="fr-label" :for="`criterium-quick-win-${item.id}-${index}`">
        Facile à corriger
      </label>
    </div>
  </div>
</template>

<style scoped>
.user-impact-label span,
.user-error-label :deep(.fr-label) {
  font-size: 0.75rem;
  color: var(--text-mention-grey);
}

.user-impact-label span {
  vertical-align: text-bottom;
}

.error-user-delete {
  text-align: right;

  button {
    color: var(--text-action-high-blue-france);
  }
}
</style>
